
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    insuranceType: 'Individual Health Plan',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('insuranceType', formData.insuranceType);
      body.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d6ep0dqff40lgbk6e260',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        }
      );

      if (!response.ok) {
        // Throw to be caught by the catch block
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        insuranceType: 'Individual Health Plan',
        message: '',
      });
    } catch (error) {
      // Log the error for debugging; UI stays silent as per original intent
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="inline-flex items-center gap-2 text-brand-600 text-sm font-semibold tracking-wider uppercase mb-3">
              <span className="w-8 h-[2px] bg-brand-500"></span>
              Contact Us
            </span>
            <h4 className="text-4xl font-display font-bold text-navy-900 mb-4 leading-tight">
              <a href="#contact">Get Your Free Private Health Insurance Quote</a>
            </h4>
            <p className="text-navy-500 text-base mb-10 leading-relaxed">
              Ready to find the perfect health insurance plan in Houston? Fill out
              the form or contact us directly. Our consultants are available{' '}
              <strong>24 hours a day, 7 days a week</strong>.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-2-fill text-xl text-brand-600"></i>
                </div>
                <div>
                  <h3 className="text-navy-900 font-semibold text-sm mb-1">
                    Visit Our Houston Office
                  </h3>
                  <p className="text-navy-500 text-sm">
                    1500 North Blvd Ste 5000 1006<br />
                    Houston, TX 77006
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-xl text-brand-600"></i>
                </div>
                <div>
                  <h3 className="text-navy-900 font-semibold text-sm mb-1">
                    Call Us Anytime
                  </h3>
                  <a
                    href="tel:7138049039"
                    className="text-brand-600 text-sm font-semibold cursor-pointer hover:text-brand-700 transition-colors"
                  >
                    (713) 804-9039
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-xl text-brand-600"></i>
                </div>
                <div>
                  <h3 className="text-navy-900 font-semibold text-sm mb-1">
                    Hours of Operation
                  </h3>
                  <p className="text-navy-500 text-sm">
                    <strong>Open 24 Hours</strong> — 7 Days a Week
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full h-[220px] rounded-xl overflow-hidden border border-navy-100">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.123456789!2d-95.3891!3d29.7392!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s1500+North+Blvd+Houston+TX+77006!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Data Health Insurance Consultants Houston TX location"
              ></iframe>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="bg-brand-50 rounded-2xl p-12 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-brand-500 flex items-center justify-center mb-6">
                  <i className="ri-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">
                  Quote Request Received!
                </h3>
                <p className="text-navy-500 text-sm mb-6">
                  Thank you for contacting Data Health Insurance Consultants.
                  One of our Houston health insurance experts will reach out to
                  you shortly.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-3 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-navy-50/50 rounded-2xl p-8 lg:p-10 border border-navy-100"
              >
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-2">
                  Request a Free Quote
                </h3>
                <p className="text-navy-500 text-sm mb-8">
                  Fill out the form below and we&apos;ll compare private health
                  insurance plans for you.
                </p>

                <div className="space-y-5">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-navy-800 text-sm font-medium mb-1.5"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder-navy-300 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-navy-800 text-sm font-medium mb-1.5"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder-navy-300 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-navy-800 text-sm font-medium mb-1.5"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder-navy-300 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="insuranceType"
                      className="block text-navy-800 text-sm font-medium mb-1.5"
                    >
                      Insurance Type *
                    </label>
                    <select
                      id="insuranceType"
                      name="insuranceType"
                      value={formData.insuranceType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors cursor-pointer"
                    >
                      <option value="Individual Health Plan">
                        Individual Health Plan
                      </option>
                      <option value="Family Coverage">Family Coverage</option>
                      <option value="Company Group Plan">Company Group Plan</option>
                      <option value="Medicare Supplement">
                        Medicare Supplement
                      </option>
                      <option value="Short-Term Insurance">
                        Short-Term Insurance
                      </option>
                      <option value="Dental & Vision">
                        Dental &amp; Vision
                      </option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-navy-800 text-sm font-medium mb-1.5"
                    >
                      Tell Us About Your Needs
                      <span className="text-navy-400 font-normal ml-1">
                        ({formData.message.length}/500)
                      </span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-white border border-navy-200 rounded-lg text-sm text-navy-900 placeholder-navy-300 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors resize-none"
                      placeholder="Tell us about your health insurance needs, budget, preferred providers, etc."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || formData.message.length > 500}
                    className="w-full py-3.5 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-600 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get My Free Quote
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  <p className="text-navy-400 text-xs text-center">
                    100% free consultation. No obligation. Your information is
                    secure.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
